<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';

const props = defineProps({
  ticket: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['back', 'close']);

const { t } = useI18n();
const store = useStore();
const alert = useAlert();

const messages = ref([]);
const replyContent = ref('');
const loading = ref(false);
const submitting = ref(false);

// 分页状态
const currentPage = ref(1);
const perPage = ref(10);
const totalPages = ref(1);

const statusMap = {
  0: 'OPEN',
  1: 'IN_PROGRESS',
  2: 'RESOLVED',
  3: 'CLOSED',
};

const statusLabel = computed(() =>
  t(`SUPPORT_TICKETS.STATUS.${statusMap[props.ticket.status] || 'OPEN'}`)
);

const canReply = computed(() => props.ticket.status !== 3);

// 分页消息
const paginatedMessages = computed(() => {
  const start = (currentPage.value - 1) * perPage.value;
  const end = start + perPage.value;
  return messages.value.slice(start, end);
});

// 计算总页数
const updateTotalPages = () => {
  totalPages.value = Math.ceil(messages.value.length / perPage.value) || 1;
};

// 可见页码
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = currentPage.value;
  const delta = 1;

  const range = [];
  const rangeWithDots = [];
  let l;

  range.push(1);

  for (
    let i = Math.max(2, current - delta);
    i <= Math.min(total - 1, current + delta);
    i += 1
  ) {
    range.push(i);
  }

  range.push(total);

  for (const i of range) {
    if (l) {
      if (i - l === 2) {
        rangeWithDots.push(l + 1);
      } else if (i - l !== 1) {
        rangeWithDots.push('...');
      }
    }
    rangeWithDots.push(i);
    l = i;
  }

  return rangeWithDots;
});

const pageHeader = computed(
  () => `#${props.ticket.ticket_no} - ${props.ticket.title}`
);

const getStatusColor = status => {
  const colorMap = {
    0: 'text-n-amber-11 bg-n-amber-2',
    1: 'text-n-blue-11 bg-n-blue-2',
    2: 'text-n-green-11 bg-n-green-2',
    3: 'text-n-slate-11 bg-n-slate-3',
  };
  return colorMap[status] || colorMap[0];
};

const formatDate = dateString => {
  return new Date(dateString).toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getPriorityLabel = priority => {
  const labels = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];
  return t(`SUPPORT_TICKETS.PRIORITY.${labels[priority] || 'MEDIUM'}`);
};

const openImagePreview = url => {
  window.open(url, '_blank');
};

const fetchMessages = async () => {
  loading.value = true;
  try {
    const response = await store.dispatch(
      'supportTickets/show',
      props.ticket.id
    );
    // show action 返回 { ...ticket, messages: [...] }
    if (Array.isArray(response)) {
      messages.value = response;
    } else if (response && Array.isArray(response.messages)) {
      messages.value = response.messages;
    } else {
      messages.value = [];
    }
    updateTotalPages();
    // 重置到第一页
    currentPage.value = 1;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch messages:', error);
    messages.value = [];
  } finally {
    loading.value = false;
  }
};

// 分页导航
const goToPage = page => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  // 滚动到消息列表顶部
  const messageListEl = document.querySelector('.message-list-container');
  if (messageListEl) {
    messageListEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const goToPrevPage = () => {
  if (currentPage.value > 1) {
    goToPage(currentPage.value - 1);
  }
};

const goToNextPage = () => {
  if (currentPage.value < totalPages.value) {
    goToPage(currentPage.value + 1);
  }
};

const handleReply = async () => {
  if (!replyContent.value.trim()) {
    return;
  }

  submitting.value = true;
  try {
    await store.dispatch('supportTickets/sendMessage', {
      ticketId: props.ticket.id,
      messageData: { content: replyContent.value },
    });
    useAlert(t('SUPPORT_TICKETS.REPLY_SUCCESS'));
    replyContent.value = '';
    await fetchMessages();
  } catch (error) {
    useAlert(t('SUPPORT_TICKETS.REPLY_ERROR'));
  } finally {
    submitting.value = false;
  }
};

const handleClose = () => {
  if (window.confirm(t('SUPPORT_TICKETS.CONFIRM_CLOSE'))) {
    emit('close', props.ticket.id);
  }
};

onMounted(() => {
  fetchMessages();
});
</script>

<template>
  <div class="flex flex-col w-full h-full gap-8">
    <!-- 头部 -->
    <BaseSettingsHeader
      :title="pageHeader"
      :description="t('SUPPORT_TICKETS.DETAIL_DESCRIPTION')"
      icon-name="arrow-chevron-left"
      :back-button-label="t('SUPPORT_TICKETS.BUTTONS.BACK')"
      @back="emit('back')"
    >
      <template #actions>
        <Button
          v-if="ticket.status !== 3"
          variant="outline"
          icon="i-lucide-check-circle"
          :label="t('SUPPORT_TICKETS.BUTTONS.CLOSE')"
          @click="handleClose"
        />
      </template>
    </BaseSettingsHeader>

    <!-- 加载状态 -->
    <div v-if="loading" class="flex-1 flex items-center justify-center">
      <woot-loading-state :message="t('SUPPORT_TICKETS.LOADING')" />
    </div>

    <!-- 主内容区域 -->
    <main v-else class="flex-1 overflow-auto">
      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <!-- 左侧：消息列表 -->
        <div class="lg:col-span-2 space-y-4">
          <!-- 工单描述 -->
          <div
            v-if="ticket.description"
            class="p-6 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <h3
              class="mb-3 text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('SUPPORT_TICKETS.LABELS.DESCRIPTION') }}
            </h3>
            <p
              class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
            >
              {{ ticket.description }}
            </p>
          </div>

          <!-- 工单附件 -->
          <div
            v-if="ticket.attachments && ticket.attachments.length > 0"
            class="p-6 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <h3
              class="mb-3 text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('SUPPORT_TICKETS.LABELS.ATTACHMENTS') }}
            </h3>
            <div class="flex flex-wrap gap-3">
              <img
                v-for="attachment in ticket.attachments"
                :key="attachment.id"
                :src="attachment.url"
                class="w-24 h-24 object-cover rounded-lg border border-n-weak cursor-pointer hover:opacity-80 transition-opacity"
                :alt="attachment.filename"
                @click="openImagePreview(attachment.url)"
              />
            </div>
          </div>

          <!-- 消息列表 -->
          <div class="message-list-container space-y-3">
            <div
              v-for="message in paginatedMessages"
              :key="message.id"
              class="p-5 rounded-lg bg-n-solid-1 border"
              :class="{
                'bg-n-amber-1 border-n-amber-4': message.is_internal,
                'border-n-blue-4': message.is_admin && !message.is_internal,
              }"
            >
              <div class="flex items-start justify-between mb-3">
                <div class="flex items-center gap-3">
                  <div
                    class="flex items-center justify-center w-8 h-8 rounded-full"
                    :class="message.is_admin ? 'bg-n-blue-2' : 'bg-n-brand-2'"
                  >
                    <span
                      class="text-sm font-medium"
                      :class="
                        message.is_admin ? 'text-n-blue-11' : 'text-n-brand'
                      "
                    >
                      {{
                        message.user?.name?.charAt(0)?.toUpperCase() ||
                        t('SUPPORT_TICKETS.UNKNOWN_USER')
                      }}
                    </span>
                  </div>
                  <div>
                    <div class="flex items-center gap-2">
                      <p class="text-sm font-medium text-n-slate-12">
                        {{
                          message.user?.name ||
                          t('SUPPORT_TICKETS.UNKNOWN_USER')
                        }}
                      </p>
                      <span
                        v-if="message.is_admin"
                        class="px-1.5 py-0.5 text-xs font-medium rounded bg-n-blue-3 text-n-blue-11"
                      >
                        {{ t('SUPPORT_TICKETS.MESSAGES.ADMIN_LABEL') }}
                      </span>
                    </div>
                    <p class="text-xs text-n-slate-10">
                      {{ formatDate(message.created_at) }}
                    </p>
                  </div>
                </div>
                <span
                  v-if="message.is_internal"
                  class="px-2 py-1 text-xs font-medium rounded bg-n-amber-3 text-n-amber-12"
                >
                  {{ t('SUPPORT_TICKETS.MESSAGES.INTERNAL_NOTE') }}
                </span>
              </div>
              <p
                class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
              >
                {{ message.content }}
              </p>

              <!-- 附件 -->
              <div
                v-if="message.attachments && message.attachments.length > 0"
                class="flex flex-wrap gap-2 mt-3"
              >
                <img
                  v-for="attachment in message.attachments"
                  :key="attachment.id"
                  :src="attachment.url"
                  class="h-24 rounded-lg border border-n-weak cursor-pointer hover:opacity-80"
                  alt="Attachment"
                  @click="openImagePreview(attachment.url)"
                />
              </div>
            </div>
          </div>

          <!-- 消息分页控件 -->
          <div
            v-if="messages.length > perPage"
            class="flex flex-col sm:flex-row items-center justify-between px-6 py-4 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <div class="text-sm text-n-slate-11">
              共 {{ messages.length }} 条消息，第 {{ currentPage }} /
              {{ totalPages }} 页
            </div>

            <div class="flex items-center gap-2">
              <!-- 上一页 -->
              <button
                :disabled="currentPage === 1"
                class="px-3 py-1.5 text-sm rounded-md border border-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-n-slate-2 text-n-slate-12"
                @click="goToPrevPage"
              >
                <span class="icon-arrow-left-line" />
              </button>

              <!-- 页码 -->
              <div class="hidden sm:flex items-center gap-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  class="px-3 py-1 text-sm rounded-md transition-colors"
                  :class="[
                    page === currentPage
                      ? 'bg-n-brand text-white'
                      : page === '...'
                        ? 'text-n-slate-11 cursor-default'
                        : 'text-n-slate-12 hover:bg-n-slate-2 border border-n-weak',
                  ]"
                  :disabled="page === '...'"
                  @click="page !== '...' && goToPage(page)"
                >
                  {{ page }}
                </button>
              </div>

              <!-- 下一页 -->
              <button
                :disabled="currentPage === totalPages"
                class="px-3 py-1.5 text-sm rounded-md border border-n-weak transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:bg-n-slate-2 text-n-slate-12"
                @click="goToNextPage"
              >
                <span class="icon-arrow-right-line" />
              </button>
            </div>
          </div>

          <!-- 回复框 -->
          <div
            v-if="canReply"
            class="p-6 rounded-lg bg-n-solid-1 border border-n-weak"
          >
            <h3
              class="mb-4 text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('SUPPORT_TICKETS.MESSAGES.REPLY_TITLE') }}
            </h3>
            <TextArea
              v-model="replyContent"
              :placeholder="t('SUPPORT_TICKETS.MESSAGES.PLACEHOLDER')"
              :rows="4"
              class="mb-4"
            />
            <div class="flex justify-end">
              <Button
                icon="i-lucide-send"
                :label="t('SUPPORT_TICKETS.BUTTONS.SEND')"
                :loading="submitting"
                :disabled="!replyContent.trim()"
                @click="handleReply"
              />
            </div>
          </div>
        </div>

        <!-- 右侧：工单信息 -->
        <div class="space-y-4">
          <!-- 状态 -->
          <div class="p-5 rounded-lg bg-n-solid-1 border border-n-weak">
            <h3
              class="mb-3 text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('SUPPORT_TICKETS.LABELS.STATUS') }}
            </h3>
            <span
              class="inline-flex px-3 py-1.5 text-sm font-medium rounded-full"
              :class="[
                getStatusColor(ticket.status)
              ]"
            >
              {{ statusLabel }}
            </span>
          </div>

          <!-- 详情 -->
          <div class="p-5 rounded-lg bg-n-solid-1 border border-n-weak">
            <h3
              class="mb-4 text-sm font-semibold text-n-slate-11 uppercase tracking-wider"
            >
              {{ t('SUPPORT_TICKETS.LABELS.DETAILS') }}
            </h3>
            <div class="space-y-4">
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ t('SUPPORT_TICKETS.LABELS.CREATED_BY') }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ ticket.user?.name || t('SUPPORT_TICKETS.UNKNOWN_USER') }}
                </p>
              </div>
              <div>
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ t('SUPPORT_TICKETS.LABELS.CREATED_AT') }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ formatDate(ticket.created_at) }}
                </p>
              </div>
              <div v-if="ticket.priority !== undefined">
                <p class="text-xs text-n-slate-10 mb-1">
                  {{ t('SUPPORT_TICKETS.LABELS.PRIORITY') }}
                </p>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ getPriorityLabel(ticket.priority) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>
